import type { ComponentPropsWithoutRef } from 'react';
import { useMemo, useCallback } from 'react';
import { useTracking } from 'react-tracking';

import { useDispatch } from 'react-redux';
import { ROUTE_PORTFOLIO_NEW } from '@/constants/routes';

import { useTheme, Button, Box, Text, Heading } from '@simplywallst/ui-core';

import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';

import { Menu, Item, Section } from '@/components/Menu';

import { Option } from './Option';
import { CreateNewPortfolioOption } from './CreateNewPortfolioOption';
import { usePortfolio } from '@/hooks/portfolio/usePortfolio';

type Key = Parameters<
  NonNullable<ComponentPropsWithoutRef<typeof Menu>['onAction']>
>[0];

interface Props {
  portfolioId: string;
  onSelect: (value: string | null) => void;
}

const CREATE_NEW_PORTFOLIO_KEY = 'create-new-portfolio';
const DEMO_PORTFOLIO_KEY = 'demo-portfolio';

export const DemoPortfolioDropdown = ({ portfolioId, onSelect }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { data: portfolio } = usePortfolio({ id: portfolioId });
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'demo-portfolio-dropDown',
  });

  const handleAction = useCallback(
    (value: Key) => {
      switch (value) {
        case CREATE_NEW_PORTFOLIO_KEY:
          trackEvent({
            action: 'click',
            modifier: 'createNewPortfolio',
          });
          dispatch({ type: ROUTE_PORTFOLIO_NEW });
          break;
        default:
          trackEvent({
            action: 'click',
            modifier: 'viewDemoPortfolio',
          });
      }
    },
    [onSelect, trackEvent]
  );

  const items = useMemo(
    () => [
      {
        label: 'Demo Portfolio',
        value: 'demo-portfolios',
        children: [
          {
            label: 'Demo portfolio',
            value: DEMO_PORTFOLIO_KEY,
            totalHoldingsCount: portfolio?.holdings.length,
          },
        ],
      },
      {
        label: 'Other options',
        value: 'other-options',
        children: [
          {
            label: 'Create new portfolio',
            value: CREATE_NEW_PORTFOLIO_KEY,
          },
        ],
      },
    ],
    [portfolio]
  );

  return (
    <Track>
      <Box transform="translateX(-12px)" maxWidth={240}>
        <Heading as="h1">
          <Menu
            label={
              <Button styleType="passive" alignIcon="right" display="block">
                <Text
                  as="span"
                  typography="baseStrong"
                  lineClamp={1}
                  color="textSolid"
                >
                  Demo Portfolio
                </Text>
                <ChevronDownSmallSolid fill={theme.color.whiteRGBA(0.5)} />
              </Button>
            }
            items={items}
            placement="bottom start"
            onAction={handleAction}
            selectionMode="single"
            selectedKeys={[portfolioId || DEMO_PORTFOLIO_KEY]}
          >
            {(item) => (
              <Section
                items={item.children}
                title={item.label}
                key={item.value}
              >
                {(item) => (
                  <Item textValue={item.label} key={item.value}>
                    {item.value === DEMO_PORTFOLIO_KEY ? (
                      <Option
                        {...item}
                        $selected={portfolioId === item.value}
                      />
                    ) : (
                      <CreateNewPortfolioOption {...item} />
                    )}
                  </Item>
                )}
              </Section>
            )}
          </Menu>
        </Heading>
      </Box>
    </Track>
  );
};
